import React, { useCallback } from "react"
import { useController, useFormContext } from "react-hook-form"

import { Autocomplete, TextField } from "@mui/material"

import cs from "classnames"
import { Option } from "./types"
import type { Props as AutocompleteProps } from "../Forms/Autocomplete/Autocomplete"
import { preventSubmitOnEnter, trackFormEvent } from "./utils"

import styles from "./Forms.module.scss"
import { Chip } from "./Autocomplete/Chip"

export interface Props extends AutocompleteProps {
  placeholder?: string
  classes?: {
    inputRoot?: string
  }
}

const Tags = ({ label, placeholder, name, rules, enableNegationOptions, className, classes, tracking }: Props) => {
  const { control, formState } = useFormContext()
  const controller = useController({ name, control, rules })
  const value = controller.field.value ?? []

  const error = formState.errors[name]
  const errorProps = error ? { error: true, label: error.message?.toString() } : {}

  const handleTagAdded = useCallback(
    async (_: unknown, value: (string | Option)[]) => {
      const _value = value.map((v) =>
        typeof v === "string"
          ? { value: v, label: v, data: enableNegationOptions ? { negative: false } : undefined }
          : v
      )

      controller.field.onChange(_value)

      if (tracking?.shouldTrackValueChange && _value.length) {
        await trackFormEvent("Form Value Provided", tracking, name, label)
      }
    },
    [controller.field, enableNegationOptions, label, name, tracking]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => !e.currentTarget.value && preventSubmitOnEnter(e),
    []
  )

  const handleOptionDeleted = useCallback(
    (option: Option) => async () => {
      const newValue = value.filter((o: Option) => o.value !== option.value)
      controller.field.onChange(newValue)
      if (tracking?.shouldTrackValueChange && newValue?.length) {
        await trackFormEvent("Form Value Provided", tracking, name, label)
      }
    },
    [controller.field, label, name, tracking, value]
  )

  const handleNegationChange = useCallback(
    (option: Option, negative: boolean) => {
      const newValue = value.map((o: Option) => (o.value === option.value ? { ...o, data: { negative } } : o))

      controller.field.onChange(newValue)
    },
    [controller.field, value]
  )

  return (
    <Autocomplete
      className={cs(styles.tags, className)}
      classes={{ inputRoot: classes?.inputRoot }}
      multiple
      options={[]}
      freeSolo
      renderInput={(params) => (
        <TextField
          label={label}
          placeholder={value?.length ? undefined : placeholder}
          {...params}
          {...errorProps}
          onKeyDown={handleKeyDown}
        />
      )}
      onChange={handleTagAdded}
      value={controller.field.value || []}
      renderTags={(options, getTagProps) => (
        <>
          {options.map((o, index) => {
            const option = typeof o === "string" ? { value: o, label: o, data: { negative: false } } : o

            return (
              <Chip
                option={option}
                key={option.value}
                onDeleteClick={handleOptionDeleted(option)}
                enableNegationOptions={enableNegationOptions}
                className={cs(getTagProps({ index }).className)}
                onNegationChange={handleNegationChange}
              />
            )
          })}
        </>
      )}
    />
  )
}

export default Tags
